// Firestore-backed PHR data layer. One ACCOUNT (uid) owns patient PROFILES
// (self + minor children); each profile can be shared with other guardians
// (a second parent on a DIFFERENT Google account) by listing their email.
//   users/{ownerUid}/profiles/{pid}              — profile doc: demographics + guardians[]
//   users/{ownerUid}/profiles/{pid}/{vitals|labs|imaging|conditions|medications|procedures|visits|notes}
//                                                — APPEND-ONLY immutable records, each w/ provider
//   shares/{id} = { ownerUid, pid, name, guardianEmail }  — discovery index for guardians
import {
  collection, getDocs, addDoc, doc, getDoc, setDoc, deleteDoc, serverTimestamp, query, where, arrayUnion, arrayRemove,
} from 'firebase/firestore';
import { fbDb, fbAuth } from './firebase';
import type {
  PHR, Owner, VitalSeries, VitalKey, LabPanel, ImagingStudy,
  Condition, Medication, Procedure, Note, Wrapper, DataSource,
} from './types';
import { resolveLandmark } from './anatomyLandmarks';
import { isDemo, demoLoadPHR, demoAdd, demoUpdate, demoDelete, demoProfilesList } from './demoMode';

// ── Encryption seam (identity now; field-level encryption later) ──────────────
const enc = <T>(o: T): T => o;
const dec = <T>(o: T): T => o;

export type RecordKind =
  | 'vitals' | 'labs' | 'imaging' | 'conditions' | 'medications' | 'procedures' | 'visits' | 'notes';

export interface Profile {
  id: string;          // pid
  ownerUid: string;    // the account that owns this profile
  firstName: string;
  lastName: string;
  dob: string;           // stored canonically as MM/DD/YYYY (see lib/regions.ts)
  sex: string;
  country?: string;      // ISO country of residence ('US' | 'IN'); defaults to 'US' for legacy records
  state?: string;        // state / UT code of residence — drives age-of-majority gating
  allergies?: string;
  relationship?: 'self' | 'child' | 'spouse' | 'parent' | 'dependent' | 'other';
  mrn?: string;
  email?: string;        // patient/contact email
  phone?: string;        // patient/contact phone
  ownerName?: string;    // name of the managing parent/guardian (the creator)
  guardians?: string[];  // emails with access (incl. the creator)
  providers?: string[];  // service-provider (doctor) emails granted access — subset of guardians, tagged for the care-team UI
  shared?: boolean;      // true if this profile is shared TO the current user
}

const profilesCol = (uid: string) => collection(fbDb, 'users', uid, 'profiles');
const recCol = (uid: string, pid: string, name: string) => collection(fbDb, 'users', uid, 'profiles', pid, name);
const clean = (o: Record<string, unknown>) => Object.fromEntries(Object.entries(o).filter(([, v]) => v !== '' && v !== undefined));

// ── Profiles ──────────────────────────────────────────────────────────────────
export async function createProfile(uid: string, email: string, data: Omit<Profile, 'id' | 'ownerUid'>): Promise<string> {
  const ref = await addDoc(profilesCol(uid), { ...enc(clean(data)), ownerUid: uid, guardians: [email], createdAt: serverTimestamp() });
  return ref.id;
}
export async function updateProfileDoc(ownerUid: string, pid: string, data: Partial<Profile>): Promise<void> {
  await setDoc(doc(fbDb, 'users', ownerUid, 'profiles', pid), enc(Object.fromEntries(Object.entries(data).filter(([, v]) => v !== undefined))), { merge: true });
}
export async function listOwnProfiles(uid: string): Promise<Profile[]> {
  if (isDemo()) return demoProfilesList();
  const snap = await getDocs(profilesCol(uid));
  return snap.docs.map((d) => ({ id: d.id, ownerUid: uid, ...(dec(d.data()) as any) })) as Profile[];
}
export async function listSharedProfiles(email: string, myUid: string): Promise<Profile[]> {
  if (isDemo()) return [];
  const e = (email || '').trim().toLowerCase();
  const snap = await getDocs(query(collection(fbDb, 'shares'), where('guardianEmail', '==', e)));
  const out: Profile[] = [];
  const seen = new Set<string>();
  for (const s of snap.docs) {
    const { ownerUid, pid } = s.data() as any;
    if (!ownerUid || !pid || ownerUid === myUid || seen.has(pid)) continue;   // skip own
    seen.add(pid);
    try {
      const p = await getDoc(doc(fbDb, 'users', ownerUid, 'profiles', pid));
      if (p.exists()) out.push({ id: pid, ownerUid, shared: true, ...(dec(p.data()) as any) } as Profile);
    } catch (_) { /* lost access */ }
  }
  return out;
}
/** Grant another parent/guardian access to a profile by email. */
export async function addGuardian(ownerUid: string, pid: string, email: string, name: string): Promise<void> {
  const e = email.trim().toLowerCase();
  await setDoc(doc(fbDb, 'users', ownerUid, 'profiles', pid), { guardians: arrayUnion(e) }, { merge: true });
  await addDoc(collection(fbDb, 'shares'), { ownerUid, pid, name, guardianEmail: e, createdAt: serverTimestamp() });
}

/** Grant a service provider (doctor) access to a profile by their Google email.
 *  Access is granted the same way as a guardian (guardians[] + a shares index doc,
 *  which the security rules honor), and the email is also tagged in providers[] so
 *  the care-team UI can list practitioners separately. They sign in with this Google
 *  email and the profile appears under their account. */
export async function addProvider(ownerUid: string, pid: string, email: string, name: string): Promise<void> {
  const e = email.trim().toLowerCase();
  await setDoc(doc(fbDb, 'users', ownerUid, 'profiles', pid),
    { guardians: arrayUnion(e), providers: arrayUnion(e) }, { merge: true });
  await addDoc(collection(fbDb, 'shares'), { ownerUid, pid, name, guardianEmail: e, role: 'provider', createdAt: serverTimestamp() });
}

/** Revoke a service provider's access: drop the email from guardians[] + providers[]
 *  and delete every shares index doc pointing this provider at the profile. */
export async function removeProvider(ownerUid: string, pid: string, email: string): Promise<void> {
  const e = email.trim().toLowerCase();
  await setDoc(doc(fbDb, 'users', ownerUid, 'profiles', pid),
    { guardians: arrayRemove(e), providers: arrayRemove(e) }, { merge: true });
  const snap = await getDocs(query(collection(fbDb, 'shares'), where('ownerUid', '==', ownerUid), where('guardianEmail', '==', e)));
  await Promise.all(snap.docs
    .filter((d) => { const v = d.data() as any; return v.ownerUid === ownerUid && v.pid === pid; })
    .map((d) => deleteDoc(d.ref)));
}

/** Revoke a guardian's access: drop the email from guardians[] and delete the
 *  guardian's (non-provider) shares index docs for this profile. */
export async function removeGuardian(ownerUid: string, pid: string, email: string): Promise<void> {
  const e = email.trim().toLowerCase();
  await setDoc(doc(fbDb, 'users', ownerUid, 'profiles', pid), { guardians: arrayRemove(e) }, { merge: true });
  const snap = await getDocs(query(collection(fbDb, 'shares'), where('ownerUid', '==', ownerUid), where('guardianEmail', '==', e)));
  await Promise.all(snap.docs
    .filter((d) => { const v = d.data() as any; return v.ownerUid === ownerUid && v.pid === pid && v.role !== 'provider'; })
    .map((d) => deleteDoc(d.ref)));
}

// ── Records (immutable, create-only) ──────────────────────────────────────────
export async function addRecord(ownerUid: string, pid: string, kind: RecordKind, data: Record<string, unknown>): Promise<string> {
  if (isDemo()) return demoAdd(pid, kind, data);
  // Stamp provenance (plaintext, outside enc) so the rules can attribute the record
  // to its creator — this is what lets a provider (doctor) edit only their OWN entries.
  const u = fbAuth.currentUser;
  const ref = await addDoc(recCol(ownerUid, pid, kind), {
    ...enc(clean(data)),
    createdByUid: u?.uid ?? null,
    createdByEmail: (u?.email ?? '').toLowerCase(),
    createdByName: u?.displayName ?? '',
    createdAt: serverTimestamp(),
  });
  return ref.id;
}
async function listAll(ownerUid: string, pid: string, name: string): Promise<any[]> {
  const snap = await getDocs(recCol(ownerUid, pid, name));
  return snap.docs.map((d) => ({ id: d.id, ...(dec(d.data()) as any) }));
}
/** Pull the creator-provenance fields off a raw record for the typed PHR objects. */
const prov = (r: any) => ({ createdByUid: r.createdByUid, createdByEmail: r.createdByEmail, createdByName: r.createdByName });

export function emptyPHR(): PHR {
  return {
    schema: 'danvantri-phr/2',
    owner: { userId: '', firstName: '', lastName: '', dob: '', sex: '', allergies: '', identifiers: [] },
    sources: [], vitals: [], labs: [], imaging: [], conditions: [], medications: [],
    procedures: [], wrappers: [], timeline: [], notes: [],
  };
}

const VITAL_DEFS: { field: string; key: VitalKey; label: string; unit: string }[] = [
  { field: 'temperature', key: 'temperature', label: 'Temperature', unit: '°F' },
  { field: 'heartRate',   key: 'hr',          label: 'Heart rate',  unit: 'bpm' },
  { field: 'systolic',    key: 'bpSystolic',  label: 'Systolic',    unit: 'mmHg' },
  { field: 'diastolic',   key: 'bpDiastolic', label: 'Diastolic',   unit: 'mmHg' },
  { field: 'respiration', key: 'rr',          label: 'Resp. rate',  unit: '/min' },
  { field: 'spo2',        key: 'spo2',        label: 'SpO₂',        unit: '%' },
  { field: 'height',      key: 'height',      label: 'Height',      unit: 'in' },
  { field: 'weight',      key: 'weight',      label: 'Weight',      unit: 'lb' },
];
function toVitalSeries(readings: any[]): VitalSeries[] {
  const out: VitalSeries[] = [];
  for (const d of VITAL_DEFS) {
    const samples = readings
      .filter((r) => r[d.field] != null && r[d.field] !== '' && Number.isFinite(Number(r[d.field])))
      .map((r) => ({ date: r.date || '', time: r.time || '', value: Number(r[d.field]), source: r.provider || 'self', _id: r.id }));
    if (samples.length) out.push({ key: d.key, label: d.label, unit: d.unit, samples });
  }
  return out;
}

/** Map a VitalKey back to the Firestore reading-doc field it's stored under. */
export const VITAL_FIELD: Partial<Record<VitalKey, string>> =
  Object.fromEntries(VITAL_DEFS.map((d) => [d.key, d.field])) as Partial<Record<VitalKey, string>>;

// ── In-place edits (mutable for now; will become append-only later) ───────────
/** Merge fields into one existing record doc (e.g. correct a lab value / vital). */
export async function updateRecord(ownerUid: string, pid: string, kind: RecordKind, docId: string, fields: Record<string, unknown>): Promise<void> {
  if (isDemo()) { demoUpdate(pid, kind, docId, fields); return; }
  await setDoc(doc(fbDb, 'users', ownerUid, 'profiles', pid, kind, docId), enc(fields), { merge: true });
}

/** Delete one record doc (e.g. a duplicate imaging study or procedure). */
export async function deleteRecord(ownerUid: string, pid: string, kind: RecordKind, docId: string): Promise<void> {
  if (isDemo()) { demoDelete(pid, kind, docId); return; }
  await deleteDoc(doc(fbDb, 'users', ownerUid, 'profiles', pid, kind, docId));
}

/** Assemble the in-memory PHR for one profile from its Firestore record. */
export async function loadPHR(ownerUid: string, pid: string): Promise<PHR> {
  if (isDemo()) return demoLoadPHR(pid);
  const [profile, vitals, labs, imaging, conditions, medications, procedures, visits, notes] = await Promise.all([
    getDoc(doc(fbDb, 'users', ownerUid, 'profiles', pid)).then((s) => (s.exists() ? (dec(s.data()) as any) : null)),
    listAll(ownerUid, pid, 'vitals'), listAll(ownerUid, pid, 'labs'), listAll(ownerUid, pid, 'imaging'),
    listAll(ownerUid, pid, 'conditions'), listAll(ownerUid, pid, 'medications'), listAll(ownerUid, pid, 'procedures'),
    listAll(ownerUid, pid, 'visits'), listAll(ownerUid, pid, 'notes'),
  ]);

  const base = emptyPHR();
  const owner: Owner = {
    ...base.owner, userId: ownerUid,
    firstName: profile?.firstName ?? '', lastName: profile?.lastName ?? '',
    dob: profile?.dob ?? '', sex: profile?.sex ?? '', allergies: profile?.allergies ?? '',
    identifiers: profile?.mrn ? [{ system: 'MRN', value: profile.mrn }] : [],
  };

  const conds: Condition[] = conditions.map((c: any) => {
    const lm = resolveLandmark(c.description || '');
    return {
      code: c.code || c.id, _id: c.id, description: c.description || '', status: c.status || 'Active',
      since: c.since || '', provider: c.provider, region: c.region, system: lm?.system ?? c.system, pos: lm?.pos,
      ...prov(c),
    };
  });

  const providers = [...new Set(
    [...vitals, ...labs, ...imaging, ...conditions, ...medications].map((r: any) => r.provider).filter(Boolean),
  )] as string[];
  const sources: DataSource[] = [
    { id: 'self', name: 'Self-entered', type: 'self' },
    ...providers.filter((p) => p !== 'self').map((p) => ({ id: p, name: p, type: 'hospital' as const })),
  ];

  return {
    ...base, owner, sources,
    vitals: toVitalSeries(vitals),
    labs: labs.map((l: any): LabPanel => ({
      code: l.code || l.panel || 'LAB', name: l.name || l.panel || 'Panel',
      date: l.date || '', source: l.provider || 'self', results: l.results || [], _id: l.id,
      ...prov(l),
    })),
    imaging: imaging.map((i: any): ImagingStudy => ({
      id: i.id, modality: i.modality || 'mri', bodyPart: i.bodyPart || '',
      date: i.date || '', source: i.provider || 'self', radiologist: i.radiologist || '', impression: i.impression || '',
      reportImportId: i.reportImportId, reportPath: i.reportPath, reportName: i.reportName,
      ...prov(i),
    })),
    conditions: conds,
    medications: medications.map((m: any): Medication => ({
      name: m.name || '', dose: m.dose, route: m.route, frequency: m.frequency,
      indication: m.indication, source: m.provider || 'self', since: m.since, until: m.until, _id: m.id,
      ...prov(m),
    })),
    procedures: procedures.map((p: any): Procedure => ({
      id: p.id, kind: p.kind || 'other', name: p.name || '', date: p.date || '',
      source: p.provider || 'self', operator: p.operator, status: p.status || 'Completed', summary: p.summary || '',
      reportImportId: p.reportImportId, reportPath: p.reportPath, reportName: p.reportName,
      ...prov(p),
    })),
    wrappers: visits.map((v: any): Wrapper => ({
      id: v.id, kind: v.kind || 'outpatient', title: v.title || '', date: v.date || '',
      endDate: v.endDate, source: v.source || v.provider || 'self', summary: v.summary || '', archived: !!v.archived,
      attachments: Array.isArray(v.attachments) ? v.attachments : [],
    })),
    timeline: [],
    notes: notes.map((n: any): Note => ({
      id: n.id, date: n.date || '', title: n.title || '', body: n.body || '', author: n.author || 'self',
      ...prov(n),
    })),
  };
}
